import { StateCreator } from 'zustand'
import { SharedSlice } from 'src/components/slice'
import { Nullable, RosteredShift, StaffMemberDetail } from '$models'
import { Dayjs } from 'dayjs'
import { rosterService, rosteredShiftService } from '$services'
import { enqueueLoadedSnackbar, exceptionMessageHandler } from '$utils'
import { DaysOfWeek } from '$constants'

interface CopyRosterState {
  sourceRosteredShifts: RosteredShift[]
  destinationRosteredShifts: RosteredShift[]
  staffMembers: StaffMemberDetail[]
  isSourceRosteredShiftsLoading: boolean
  isDestinationRosteredShiftsLoading: boolean
}

export interface CopyRosterSlice extends CopyRosterState {
  /**
   * Reset CopyRoster Slice
   */
  resetCopyRosterSlice: () => void

  /**
   * Get Rosters by date range
   * @param startTime
   * @param endTime
   * @param type 'source' | 'destination' this helps us to know which rostered shifts
   * we need to populate (the source or destination rostered shifts)
   * @returns Promise<RosteredShift[]>
   */
  getRosteredShitsByDateRange: (
    startDateTime: Dayjs | null,
    endDateTime: Dayjs | null,
    type: 'source' | 'destination'
  ) => Promise<RosteredShift[]>

  /**
   * Copy Roster
   * @param rosterTemplateId
   * @param sourceWeekStart
   * @param destinationWeekStart
   * @param days
   * @param staffMemberRosteredShiftsInfo
   * @returns Promise<boolean>
   */
  copyRoster: (
    rosterTemplateId: Nullable<string>,
    sourceWeekStart: Nullable<Dayjs>,
    destinationWeekStart: Dayjs,
    days: DaysOfWeek[],
    staffMemberRosteredShiftsInfo: rosterService.StaffMemberRosteredShiftsInfo[],
    successMessage?: string,
    errorMessage?: string
  ) => Promise<boolean>

  /**
   * Set Rostered Shifts
   * @param type 'source' | 'destination' this helps us to know which rostered shifts
   * we need to populate (the source or destination rostered shifts)
   * @param rosteredShifts RosteredShift[] to set
   */
  setRosteredShifts: (
    type: 'source' | 'destination',
    rosteredShifts: RosteredShift[]
  ) => void
}

const initialState: CopyRosterState = {
  sourceRosteredShifts: [],
  destinationRosteredShifts: [],
  staffMembers: [],
  isSourceRosteredShiftsLoading: false,
  isDestinationRosteredShiftsLoading: false,
}

const createCopyRosterSlice: StateCreator<
  CopyRosterSlice & SharedSlice,
  [],
  [],
  CopyRosterSlice
> = (set) => ({
  // State variables
  ...initialState,

  // State setters
  resetCopyRosterSlice: () => set(initialState),

  /**
   * We get the rostered shifts from the roster itself
   */
  getRosteredShitsByDateRange: async (
    startTime: Dayjs | null,
    endTime: Dayjs | null,
    type: 'source' | 'destination'
  ): Promise<RosteredShift[]> => {
    if (!startTime || !endTime) {
      if (type === 'source') {
        set(() => ({
          sourceRosteredShifts: [],
        }))
      } else if (type === 'destination') {
        set(() => ({
          destinationRosteredShifts: [],
        }))
      }
      return []
    }

    if (type === 'source') {
      set(() => ({
        isSourceRosteredShiftsLoading: true,
      }))
    } else if (type === 'destination') {
      set(() => ({
        isDestinationRosteredShiftsLoading: true,
      }))
    }

    return rosteredShiftService
      .getAll(startTime, endTime)
      .then((result) => {
        if (type === 'source') {
          set(() => ({
            sourceRosteredShifts: result,
            isSourceRosteredShiftsLoading: false,
          }))
        } else if (type === 'destination') {
          set(() => ({
            destinationRosteredShifts: result,
            isDestinationRosteredShiftsLoading: false,
          }))
        }
        return result
      })
      .catch((error) => {
        set(() => ({
          sourceRosteredShifts: [],
        }))
        exceptionMessageHandler(error, 'Failed to get the roster')
        return []
      })
      .finally(() => {
        set(() => ({
          isSourceRosteredShiftsLoading: false,
          isDestinationRosteredShiftsLoading: false,
        }))
      })
  },

  copyRoster: async (
    rosterTemplateId: Nullable<string>,
    sourceWeekStart: Nullable<Dayjs>,
    destinationWeekStart: Dayjs,
    days: DaysOfWeek[],
    staffMemberRosteredShiftsInfo: rosterService.StaffMemberRosteredShiftsInfo[],
    successMessage?: string,
    errorMessage?: string
  ): Promise<boolean> => {
    set(() => ({
      isLoading: true,
    }))
    return rosterService
      .copyRoster(
        rosterTemplateId,
        sourceWeekStart,
        destinationWeekStart,
        days,
        staffMemberRosteredShiftsInfo
      )
      .then(() => {
        // We don't set isLoading to false here because we want to redirect to the
        // angular page without flashing the copy page first
        enqueueLoadedSnackbar(successMessage, 'success')
        return true
      })
      .catch((error) => {
        exceptionMessageHandler(error, errorMessage)
        set(() => ({
          isLoading: false,
        }))
        return false
      })
  },

  setRosteredShifts: (
    type: 'source' | 'destination',
    rosteredShifts: RosteredShift[]
  ): void => {
    if (type === 'source') {
      set(() => ({
        sourceRosteredShifts: rosteredShifts,
      }))
    } else if (type === 'destination') {
      set(() => ({
        destinationRosteredShifts: rosteredShifts,
      }))
    }
  },
})

export default createCopyRosterSlice
